import { useState } from "react"
import type { ClipboardEvent } from "react"
import { flushSync } from "react-dom"
import type { TenantFragment } from "@/v2-backoffice-client/tenant/__types__/tenant.fragment"
import {
  parseClipboardTable,
  setNativeValue,
} from "@/v2-backoffice-client/components/bulkForm/bulkForm.hooks"
import { parseEmails } from "@/v2-backoffice-client/utils"
import {
  BULK_ROWS_OPTIONS,
  type InvitationRow,
} from "@/v2-backoffice-client/invitation/invitation.constants"

export type InvitationSection = {
  key: string
  tenant: TenantFragment | null
  rows: InvitationRow[]
  /** Present on every section born from a spreadsheet import — drives the
   *  match-status badge and the send-time auto-create. */
  imported?: {
    teamName: string
    candidates: TenantFragment[]
    suggestedCustomer: {
      ExternalCustomerId: string
      Name?: string | null
    } | null
    /** Operator confirmed the auto-match (assurance only, never blocks). */
    verified?: boolean
  }
}

/**
 * How the section's team stands, driving the status badge and what send
 * does with it:
 * - manual: operator-owned pick (hand-added section, an import row with no
 *   team name, or a cleared auto-match) — send requires a team.
 * - matched/verified: the import auto-matched exactly one existing team;
 *   verified is the operator's explicit thumbs-up on top.
 * - ambiguous: several teams share the imported name — send blocks until
 *   the operator picks one.
 * - create: nothing matched — send creates the team (and its customer,
 *   when none matched by name) automatically.
 */
export type InvitationSectionMatchStatus =
  | "manual"
  | "matched"
  | "verified"
  | "ambiguous"
  | "create"

export function sectionMatchStatus(
  section: InvitationSection,
): InvitationSectionMatchStatus {
  if (!section.imported) return "manual"
  if (section.tenant) return section.imported.verified ? "verified" : "matched"
  if (!section.imported.teamName) return "manual"
  if (section.imported.candidates.length > 1) return "ambiguous"
  // Exactly one candidate but no tenant: the operator cleared the
  // auto-match on purpose, so the pick is theirs now.
  if (section.imported.candidates.length === 1) return "manual"
  return "create"
}

function createSection(): InvitationSection {
  return {
    key: crypto.randomUUID(),
    tenant: null,
    rows: [BULK_ROWS_OPTIONS.createEmptyRow()],
  }
}

/** The data attribute the paste handler resolves a section from. */
export const SECTION_INDEX_ATTR = "data-invitation-section"

/**
 * Section state for the multi-team invite dialog: one section per target
 * team, each with its own row grid. The paste handler mirrors
 * useBulkRows.handlePaste, scoped to the section the paste landed in.
 */
export function useInvitationSections() {
  const [sections, setSections] = useState<InvitationSection[]>([
    createSection(),
  ])

  function addSection() {
    setSections((prev) => [...prev, createSection()])
  }

  function removeSection(index: number) {
    setSections((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== index) : prev,
    )
  }

  function setSectionTenant(index: number, tenant: TenantFragment | null) {
    setSections((prev) =>
      prev.map((s, i) => (i === index ? { ...s, tenant } : s)),
    )
  }

  function setSectionVerified(index: number, verified: boolean) {
    setSections((prev) =>
      prev.map((s, i) =>
        i === index && s.imported
          ? { ...s, imported: { ...s.imported, verified } }
          : s,
      ),
    )
  }

  function addRow(index: number) {
    setSections((prev) =>
      prev.map((s, i) =>
        i === index
          ? { ...s, rows: [...s.rows, BULK_ROWS_OPTIONS.createEmptyRow()] }
          : s,
      ),
    )
  }

  function removeRow(index: number, rowIndex: number) {
    setSections((prev) =>
      prev.map((s, i) => {
        if (i !== index) return s
        const rows = s.rows.filter((_, r) => r !== rowIndex)
        return {
          ...s,
          rows: rows.length ? rows : [BULK_ROWS_OPTIONS.createEmptyRow()],
        }
      }),
    )
  }

  function updateRow<K extends keyof InvitationRow>(
    index: number,
    rowIndex: number,
    field: K,
    value: InvitationRow[K],
  ) {
    setSections((prev) =>
      prev.map((s, i) =>
        i === index
          ? {
              ...s,
              rows: s.rows.map((row, r) =>
                r === rowIndex ? { ...row, [field]: value } : row,
              ),
            }
          : s,
      ),
    )
  }

  function reset() {
    setSections([createSection()])
  }

  /** Replaces the whole board — the spreadsheet-import path. */
  function loadSections(loaded: InvitationSection[]) {
    setSections(loaded.length ? loaded : [createSection()])
  }

  // Table-paste, scoped to the section the paste landed in — same TSV
  // fan-out contract as useBulkRows.handlePaste (see bulkForm.hooks).
  function handlePaste(e: ClipboardEvent) {
    const target = e.target
    if (
      !(target instanceof HTMLInputElement) &&
      !(target instanceof HTMLTextAreaElement)
    ) {
      return
    }

    const sectionEl = target.closest(`[${SECTION_INDEX_ATTR}]`)
    const sectionIndex = Number(sectionEl?.getAttribute(SECTION_INDEX_ATTR))
    if (!sectionEl || Number.isNaN(sectionIndex)) return

    const td = target.closest("td")
    const tr = td?.closest("tr") ?? null
    const tbody = tr?.parentElement ?? null
    if (!td || !tr || !tbody) return

    const text = e.clipboardData.getData("text/plain")
    let table = parseClipboardTable(text)
    if (table.length === 1 && table[0].length === 1) {
      const items = parseEmails(table[0][0])
      if (items.length <= 1) return
      table = items.map((item) => [item])
    }

    e.preventDefault()

    const sourceRowIdx = Array.prototype.indexOf.call(tbody.children, tr)
    const sourceColIdx = Array.prototype.indexOf.call(tr.children, td)
    if (sourceRowIdx < 0 || sourceColIdx < 0) return

    const neededRows = sourceRowIdx + table.length
    flushSync(() => {
      setSections((prev) =>
        prev.map((s, i) => {
          if (i !== sectionIndex || s.rows.length >= neededRows) return s
          const extras = Array.from(
            { length: neededRows - s.rows.length },
            () => BULK_ROWS_OPTIONS.createEmptyRow(),
          )
          return { ...s, rows: [...s.rows, ...extras] }
        }),
      )
    })

    for (let r = 0; r < table.length; r++) {
      const targetTr = tbody.children[sourceRowIdx + r]
      if (!targetTr) continue
      const cells = table[r]
      for (let c = 0; c < cells.length; c++) {
        const targetTd = targetTr.children[sourceColIdx + c]
        if (!targetTd) continue
        const cellInput = targetTd.querySelector<
          HTMLInputElement | HTMLTextAreaElement
        >("input, textarea")
        if (!cellInput) continue
        setNativeValue(cellInput, cells[c])
      }
    }
  }

  return {
    sections,
    addSection,
    removeSection,
    setSectionTenant,
    setSectionVerified,
    addRow,
    removeRow,
    updateRow,
    reset,
    loadSections,
    handlePaste,
  }
}
